import { CaretRightIcon, MagnifyingGlassIcon, TrayIcon } from '@phosphor-icons/react'
import {
  AlertBanner,
  Button,
  EmptyState,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
  Spinner,
} from '@curri/components'
import { useMemo, useState } from 'react'

import type { Resource } from '../../api/client.ts'
import type { Booking, BookingStatusFilter } from '../../api/bookings.ts'
import { Panel, PANEL_HAIRLINE } from '../webhooks/Panel.tsx'
import { Pill } from '../webhooks/Pill.tsx'

import { BookingDetail } from './BookingDetail.tsx'
import {
  describeFailure,
  formatBookedDate,
  formatBookedTime,
  formatPrice,
  primaryReference,
  searchHaystack,
  STATUS_PILL,
  UNKNOWN_STATUS_PILL,
  stopCity,
  stopLine,
} from './format.ts'

// The bookings table: the design's 7-column grid with an expandable two-pane
// detail. Same construction as the webhooks AttemptsCard — a CSS grid with
// ARIA table roles, because the BUILD look (floating header bar, rounded hover
// rows, a detail that unfolds under the row) is not something the table
// component's slots express.

const STATUS_FILTERS: { label: string; value: BookingStatusFilter }[] = [
  { label: 'All statuses', value: 'all' },
  { label: 'Ready', value: 'ready' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Canceled', value: 'failed' },
]

const GRID = {
  display: 'grid',
  gridTemplateColumns:
    '30px minmax(132px, 176px) minmax(150px, 1fr) minmax(150px, 1fr) minmax(84px, 112px) minmax(94px, 116px) minmax(88px, 104px)',
  gap: 12,
  alignItems: 'center',
} as const

/** The row's leading identity: the partner's own reference where there is one
 *  (that is what they recognize), else Curri's id. */
const rowReference = (booking: Booking): string =>
  primaryReference(booking) ?? booking.id

const TwoLineCell = ({
  primary,
  secondary,
  mono = false,
}: {
  primary: string
  secondary: string | null
  mono?: boolean
}) => (
  <span style={{ minWidth: 0 }}>
    <span
      style={{
        display: 'block',
        fontFamily: mono ? 'var(--curri-font-mono)' : undefined,
        fontSize: mono ? 13 : 13.5,
        fontWeight: mono ? 600 : 400,
        color: 'var(--curri-text)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {primary}
    </span>
    {secondary !== null && (
      <span
        style={{
          display: 'block',
          marginTop: 3,
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 11,
          color: 'var(--curri-text-tertiary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {secondary}
      </span>
    )}
  </span>
)

const PriceCell = ({ booking }: { booking: Booking }) => {
  const price = formatPrice(booking.price)
  return (
    <span style={{ textAlign: 'right' }}>
      <span
        style={{
          display: 'block',
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 13,
          color: price === null ? 'var(--curri-text-tertiary)' : 'var(--curri-text)',
        }}
      >
        {price ?? '—'}
      </span>
      <span
        style={{
          display: 'block',
          marginTop: 3,
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 11,
          color: 'var(--curri-text-tertiary)',
        }}
      >
        {price === null
          ? 'not recorded'
          : booking.status.code === 'failed'
            ? 'not billed'
            : 'final'}
      </span>
    </span>
  )
}

const BookingRow = ({
  booking,
  isExpanded,
  onToggle,
}: {
  booking: Booking
  isExpanded: boolean
  onToggle: () => void
}) => {
  // By lookup, not by assertion: see UNKNOWN_STATUS_PILL.
  const pill =
    STATUS_PILL[booking.status.code] ?? UNKNOWN_STATUS_PILL(booking.status.code)

  return (
    <div role="rowgroup" style={{ borderBottom: `1px solid ${PANEL_HAIRLINE}` }}>
      <div
        className="booking-row"
        onClick={onToggle}
        role="row"
        style={{ ...GRID, padding: '11px 12px', cursor: 'pointer' }}
      >
        <span
          role="cell"
          style={{ display: 'inline-flex', color: 'var(--curri-text-tertiary)' }}
        >
          {/* A real button so the expander is keyboard-operable; no handler of
              its own — Enter/Space synthesize a click that bubbles to the row,
              so pointer and keyboard share the one toggle path. */}
          <button
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} booking details for ${rowReference(booking)}`}
            className="booking-expand"
            type="button"
          >
            <CaretRightIcon
              className="booking-chevron"
              size={16}
              style={{ transform: isExpanded ? 'rotate(90deg)' : undefined }}
            />
          </button>
        </span>
        <span role="cell" style={{ minWidth: 0 }}>
          <TwoLineCell
            mono
            primary={rowReference(booking)}
            secondary={booking.id}
          />
        </span>
        <span role="cell" style={{ minWidth: 0 }}>
          <TwoLineCell
            primary={stopLine(booking.pickup)}
            secondary={stopCity(booking.pickup)}
          />
        </span>
        <span role="cell" style={{ minWidth: 0 }}>
          <TwoLineCell
            primary={stopLine(booking.dropoff)}
            secondary={stopCity(booking.dropoff)}
          />
        </span>
        <span role="cell">
          <PriceCell booking={booking} />
        </span>
        <span role="cell">
          <TwoLineCell
            mono
            primary={formatBookedDate(booking.createdAt)}
            secondary={formatBookedTime(booking.createdAt)}
          />
        </span>
        <span
          role="cell"
          style={{
            display: 'inline-flex',
            justifySelf: 'end',
            alignItems: 'center',
            // Wraps rather than overflowing: a sandbox row carries two pills,
            // and the column is sized for one — unwrapped, the second spilled
            // left over the Booked column.
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: 4,
          }}
        >
          {booking.isSandbox && <Pill tone="neutral">Sandbox</Pill>}
          <Pill tone={pill.tone}>{pill.label}</Pill>
        </span>
      </div>
      {isExpanded && (
        <div className="booking-open" role="row">
          <div aria-colspan={7} role="cell">
            <BookingDetail booking={booking} />
          </div>
        </div>
      )}
    </div>
  )
}

const HeaderRow = () => (
  <div
    role="row"
    style={{
      ...GRID,
      padding: '9px 12px',
      background: 'var(--curri-canvas)',
      borderRadius: 8,
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--curri-text-tertiary)',
    }}
  >
    <span aria-label="Expand" role="columnheader" />
    <span role="columnheader">Reference</span>
    <span role="columnheader">Pickup</span>
    <span role="columnheader">Dropoff</span>
    <span role="columnheader" style={{ textAlign: 'right' }}>
      Price
    </span>
    <span role="columnheader">Booked</span>
    <span role="columnheader" style={{ textAlign: 'right' }}>
      Status
    </span>
  </div>
)

const NoBookings = ({
  isFiltered,
}: {
  /** A search term or a non-`all` status is active. */
  isFiltered: boolean
}) => (
  <EmptyState size="small">
    <EmptyStateIcon>
      <TrayIcon />
    </EmptyStateIcon>
    <EmptyStateTitle>
      {isFiltered ? 'No bookings match that filter' : 'No API bookings yet'}
    </EmptyStateTitle>
    <EmptyStateDescription>
      {isFiltered
        ? 'Try a different search, or clear the status filter.'
        : 'Orders created through the Curri API land here. Book one through the REST API and it appears with its request, identifiers, and webhook history.'}
    </EmptyStateDescription>
  </EmptyState>
)

export const BookingsCard = ({
  isLoadingMore,
  loadMore,
  loadMoreFailed,
  nextCursor,
  onStatusChange,
  resource,
  status,
}: {
  isLoadingMore: boolean
  loadMore: () => void
  loadMoreFailed: boolean
  nextCursor: string | null
  onStatusChange: (next: BookingStatusFilter) => void
  resource: Resource<Booking[]>
  status: BookingStatusFilter
}) => {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const [query, setQuery] = useState('')

  const toggle = (id: string) =>
    setExpanded(current => {
      const next = new Set(current)
      if (!next.delete(id)) next.add(id)
      return next
    })

  // Search is client-side over the loaded rows, like the design's mock: the
  // list is the caller's own bookings and a page is 25 rows, so a server round
  // trip per keystroke buys nothing yet. Revisit alongside server-side search.
  const loaded = resource.status === 'ready' ? resource.data : []
  const needle = query.trim().toLowerCase()
  const visible = useMemo(
    () =>
      needle === ''
        ? loaded
        : loaded.filter(booking => searchHaystack(booking).includes(needle)),
    [loaded, needle]
  )

  return (
    <Panel style={{ paddingTop: 34, paddingBottom: 32 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '10px 12px',
        }}
      >
        <div style={{ position: 'relative', flex: '1 1 250px', minWidth: 210, maxWidth: 330 }}>
          <span
            style={{
              position: 'absolute',
              left: 11,
              top: 0,
              bottom: 0,
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--curri-text-tertiary)',
              pointerEvents: 'none',
            }}
          >
            <MagnifyingGlassIcon size={16} />
          </span>
          <input
            aria-label="Search bookings"
            className="booking-search"
            onChange={event => setQuery(event.target.value)}
            placeholder="Reference, city, item…"
            type="text"
            value={query}
          />
        </div>
        <select
          aria-label="Filter by status"
          className="booking-status-select"
          onChange={event => onStatusChange(event.target.value as BookingStatusFilter)}
          value={status}
        >
          {STATUS_FILTERS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span style={{ flex: 1 }} />
        {resource.status === 'ready' && (
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11,
              color: 'var(--curri-text-tertiary)',
            }}
          >
            showing {visible.length} of {loaded.length}
            {nextCursor !== null ? ' loaded' : ''}
          </span>
        )}
      </div>

      {resource.status === 'loading' && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spinner size="lg" />
        </div>
      )}

      {resource.status === 'failed' &&
        (() => {
          const copy = describeFailure(resource.failure)
          return (
            <div style={{ marginTop: 16 }}>
              <AlertBanner title={copy.title} type={copy.tone}>
                {copy.body}
              </AlertBanner>
            </div>
          )
        })()}

      {resource.status === 'ready' &&
        (visible.length === 0 ? (
          <div style={{ marginTop: 16 }}>
            <NoBookings isFiltered={needle !== '' || status !== 'all'} />
          </div>
        ) : (
          <div
            aria-label="Bookings"
            role="table"
            style={{ marginTop: 18, overflowX: 'auto' }}
          >
            <div style={{ minWidth: 880 }}>
              <HeaderRow />
              {visible.map(booking => (
                <BookingRow
                  booking={booking}
                  isExpanded={expanded.has(booking.id)}
                  key={booking.id}
                  onToggle={() => toggle(booking.id)}
                />
              ))}
            </div>
          </div>
        ))}

      {loadMoreFailed && (
        <div style={{ marginTop: 12 }}>
          <AlertBanner type="danger">
            Could not load more bookings. The ones already shown are unaffected.
          </AlertBanner>
        </div>
      )}

      {resource.status === 'ready' && loaded.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 16,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--curri-font-mono)',
              fontSize: 11.5,
              color: 'var(--curri-text-tertiary)',
            }}
          >
            {nextCursor === null
              ? `Showing ${loaded.length} — that is everything`
              : `Showing ${loaded.length}`}
          </span>
          {nextCursor !== null && (
            <Button
              label={isLoadingMore ? 'Loading…' : 'Load more'}
              loading={isLoadingMore}
              onClick={loadMore}
              size="small"
              variant="secondary"
            />
          )}
        </div>
      )}
    </Panel>
  )
}
